import { z } from "zod";

import { ISO_DATE_PATTERN, ISO_INSTANT_PATTERN, isIsoDate, isIsoInstant } from "@blog/shared";

/**
 * Primitive value objects shared by every domain type.
 *
 * These are the guard rails for SPEC §60 (path security) and AGENTS.md §9/§10
 * (locale and slug rules). Nothing may reach D1 or a Git tree without passing
 * through them.
 */

// ---------------------------------------------------------------------------
// Identifiers
// ---------------------------------------------------------------------------

/**
 * A stable entity identifier.
 *
 * Accepts both canonical ULIDs and the shorter opaque ids used in SPEC examples
 * (`01JABC123456789`), because ids are opaque and only their stability matters
 * (AGENTS.md §8). New ids are always minted as ULIDs by `generateId`.
 */
export const entityIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{7,63}$/, "Must be an 8-64 character opaque identifier");

export type EntityId = z.infer<typeof entityIdSchema>;

/** A Git object SHA — short or full. */
export const gitShaSchema = z
  .string()
  .regex(/^[0-9a-f]{7,64}$/, "Must be a lowercase hexadecimal Git object SHA");

/** A SHA-256 digest of a byte sequence, hex encoded. */
export const sha256Schema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, "Must be a lowercase hexadecimal SHA-256 digest");

// ---------------------------------------------------------------------------
// Locale
// ---------------------------------------------------------------------------

/**
 * Canonical BCP-47-like locale: language, optional script, region.
 *
 * `zh-CN`, `en-US`, `ja-JP`, `de-DE`, `zh-Hans-CN`, `es-419`.
 *
 * Deliberately rejects the loose forms AGENTS.md §9 calls out — `zh`, `CN`,
 * `zh_cn`, `english` — unless a locale mapping layer canonicalizes them first
 * via `normalizeLocale`.
 */
export const LOCALE_PATTERN = /^[a-z]{2,3}(?:-[A-Z][a-z]{3})?(?:-[A-Z]{2}|-\d{3})$/;

export const localeSchema = z
  .string()
  .regex(LOCALE_PATTERN, "Must be a canonical locale such as zh-CN or en-US");

export type LocaleCode = z.infer<typeof localeSchema>;

/** BCP-47 `language[-script][-region]`, plus the legacy underscore separator. */
const LOOSE_LOCALE_PATTERN = /^([A-Za-z]{2,3})(?:[_-]([A-Za-z]{4}))?(?:[_-]([A-Za-z]{2}|\d{3}))?$/;

/**
 * Best-effort canonicalization for the locale mapping layer (AGENTS.md §9).
 *
 * Returns `null` when the input cannot be mapped to a canonical locale — the
 * caller must decide whether that is an error, never silently fall back.
 *
 *   "ZH_cn"  -> "zh-CN"
 *   "en-us"  -> "en-US"
 *   "zh"     -> null   (region is required)
 *   "CN"     -> null   (language is required)
 */
export function normalizeLocale(input: string): LocaleCode | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const match = LOOSE_LOCALE_PATTERN.exec(trimmed);
  if (!match) {
    return null;
  }

  const language = match[1]!.toLowerCase();
  const script = match[2];
  const region = match[3];

  // A region is mandatory: SPEC §10 builds URLs as `/{locale}/...` and SPEC §11
  // forbids implicit fallback, so an ambiguous locale must fail loudly.
  if (!region) {
    return null;
  }

  const canonicalRegion = /^\d{3}$/.test(region) ? region : region.toUpperCase();
  const canonicalScript = script ? script[0]!.toUpperCase() + script.slice(1).toLowerCase() : "";
  const candidate = canonicalScript
    ? `${language}-${canonicalScript}-${canonicalRegion}`
    : `${language}-${canonicalRegion}`;

  return LOCALE_PATTERN.test(candidate) ? candidate : null;
}

/** Locale as it appears in a URL path segment: `zh-CN` -> `zh-cn`. */
export function localeToUrlSegment(locale: LocaleCode): string {
  return locale.toLowerCase();
}

/** Inverse of `localeToUrlSegment`, validating that the result is canonical. */
export function localeFromUrlSegment(segment: string): LocaleCode | null {
  return normalizeLocale(segment);
}

// ---------------------------------------------------------------------------
// Slug
// ---------------------------------------------------------------------------

/**
 * Directory names inside the content repository (SPEC §5.2).
 *
 * A page slug shares the `/{locale}/` namespace with the posts index, so these
 * can never be used as a slug without breaking routing.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  "posts",
  "pages",
  "assets",
  "config",
  "tags",
  "categories",
]);

const SLUG_PATTERN = /^[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*$/u;

export const SLUG_MAX_LENGTH = 120;

/**
 * A URL-safe, normalized, deterministic slug (AGENTS.md §10).
 *
 * Guarantees:
 *   - no path separators, dots, null bytes or traversal sequences
 *   - lowercase only (slugs are compared case-insensitively by URL consumers)
 *   - no leading/trailing/consecutive dashes
 *   - not a reserved content-repository directory name
 */
export const slugSchema = z
  .string()
  .min(1, "Slug must not be empty")
  .max(SLUG_MAX_LENGTH, `Slug must be at most ${String(SLUG_MAX_LENGTH)} characters`)
  .refine((value) => value === value.toLowerCase(), {
    message: "Slug must be lowercase",
  })
  .refine((value) => SLUG_PATTERN.test(value), {
    message: "Slug may contain letters, numbers and single dashes only",
  })
  .refine((value) => !RESERVED_SLUGS.has(value), {
    message: "Slug collides with a reserved content repository directory",
  });

export type Slug = z.infer<typeof slugSchema>;

/** True when `value` is a valid slug. Non-throwing companion to `slugSchema`. */
export function isValidSlug(value: unknown): value is string {
  return typeof value === "string" && slugSchema.safeParse(value).success;
}

// ---------------------------------------------------------------------------
// URLs and hosts
// ---------------------------------------------------------------------------

/** An absolute `https://` URL. */
export const httpsUrlSchema = z
  .string()
  .url("Must be an absolute URL")
  .refine((value) => value.startsWith("https://"), {
    message: "Must use the https scheme",
  });

/**
 * A bare hostname, no scheme and no path.
 *
 * `example.com`, `www.example.com`, `img.example.com`.
 */
export const hostnameSchema = z
  .string()
  .min(3)
  .max(253)
  .regex(
    /^(?=.{3,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/,
    "Must be a lowercase hostname such as www.example.com",
  );

/**
 * A repository-relative path.
 *
 * Rejects absolute paths, traversal, backslashes and null bytes. This is the
 * schema-level half of SPEC §60; `assertSafeRepoPath` in `@blog/shared` is the
 * runtime half used when actually building Git trees.
 */
export const repoPathSchema = z
  .string()
  .min(1)
  .max(1024)
  .refine((value) => !value.startsWith("/"), { message: "Must be repository-relative" })
  .refine((value) => !value.includes("\\"), { message: "Must use forward slashes only" })
  .refine((value) => !value.includes("\0"), { message: "Must not contain a null byte" })
  .refine((value) => !/^[A-Za-z][A-Za-z0-9+.-]*:/.test(value), {
    message: "Must not contain a drive letter or URL scheme",
  })
  .refine(
    (value) =>
      value
        .split("/")
        .every((segment) => segment !== "" && segment !== "." && segment !== ".."),
    { message: "Must not contain empty, '.' or '..' segments" },
  );

// ---------------------------------------------------------------------------
// Dates and times
// ---------------------------------------------------------------------------

/** An ISO-8601 UTC instant, as stored in D1 TEXT columns. */
export const isoInstantSchema = z
  .string()
  .regex(ISO_INSTANT_PATTERN, "Must be an ISO-8601 UTC instant such as 2026-09-23T00:00:00Z")
  .refine(isIsoInstant, { message: "Must be a real ISO-8601 UTC instant" });

/** A bare `YYYY-MM-DD` calendar date, as used in Markdown frontmatter. */
export const isoDateSchema = z
  .string()
  .regex(ISO_DATE_PATTERN, "Must be a YYYY-MM-DD calendar date")
  .refine(isIsoDate, { message: "Must be a real calendar date" });

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Publication status of a single localization (SPEC §9.1). */
export const contentStatusSchema = z.enum(["draft", "published", "scheduled", "archived"]);

export type ContentStatus = z.infer<typeof contentStatusSchema>;

/** A content entry is either an article or a standalone page (SPEC §40). */
export const contentTypeSchema = z.enum(["post", "page"]);

export type ContentType = z.infer<typeof contentTypeSchema>;

/** Media types accepted by the upload endpoint (SPEC §18). SVG is off. */
export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

export const imageMimeTypeSchema = z.enum(ALLOWED_IMAGE_MIME_TYPES);

export type ImageMimeType = z.infer<typeof imageMimeTypeSchema>;

/**
 * Attachment types accepted by the upload endpoint.
 *
 * Deliberately narrow, and SVG is off here for the same reason it is off for
 * images: an allow-list the team can read in one screen is one nobody has to
 * audit. `text/plain` only arrives as an explicit file upload — pasted clipboard
 * text never becomes a `File`.
 */
export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  "application/pdf",
  "application/zip",
  "text/plain",
  "text/csv",
  "audio/mpeg",
  "video/mp4",
] as const;

export const attachmentMimeTypeSchema = z.enum(ALLOWED_ATTACHMENT_MIME_TYPES);

export type AttachmentMimeType = z.infer<typeof attachmentMimeTypeSchema>;

/** Everything the upload endpoint accepts: images and attachments. */
export const mediaMimeTypeSchema = z.enum([
  ...ALLOWED_IMAGE_MIME_TYPES,
  ...ALLOWED_ATTACHMENT_MIME_TYPES,
]);

export type MediaMimeType = z.infer<typeof mediaMimeTypeSchema>;

/** True when a media MIME type renders inline as an image. */
export function isImageMimeType(mimeType: string): boolean {
  return (ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(mimeType.toLowerCase());
}

/** Hard upload ceiling (SPEC §18). */
export const MAX_MEDIA_BYTES = 10 * 1024 * 1024;
